import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AdminLayout from "../../../../components/layout/AdminLayout";
import { App } from "antd";
import XMarkdown from "@ant-design/x-markdown";
import {
  type FeedbackDetail,
  getFeedbackDetail,
  updateInternalNote,
  parseFeedbackFlags,
} from "../../../../services/feedback";

export default function FeedbackDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const { message } = App.useApp();

  const [feedback, setFeedback] = useState<FeedbackDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAnswerExpanded, setIsAnswerExpanded] = useState(false);
  const [internalNote, setInternalNote] = useState("");
  const [isEditingNote, setIsEditingNote] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await getFeedbackDetail(id);
      setFeedback(res.data);
      setInternalNote(res.data.InternalNote || "");
    } catch {
      message.error(t("common.loadError"));
    } finally {
      setLoading(false);
    }
  }, [id, t, message]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <i className="ri-loader-4-line animate-spin text-2xl text-gray-400"></i>
        </div>
      </AdminLayout>
    );
  }

  if (!feedback) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-gray-500">{t("feedbackDetail.notFound")}</p>
        </div>
      </AdminLayout>
    );
  }

  const handleSaveNote = async () => {
    try {
      await updateInternalNote(feedback.ID, internalNote);
      setFeedback((prev) => (prev ? { ...prev, InternalNote: internalNote } : prev));
      setIsEditingNote(false);
      message.success(t("feedbackDetail.noteSaved"));
    } catch {
      message.error(t("common.saveError"));
    }
  };

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return "-";
    const d = new Date(new Date(dateStr).toLocaleString());
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const h = String(d.getHours()).padStart(2, "0");
    const m = String(d.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day} ${h}:${m}`;
  };

  const isUseless = feedback.Type === "useless";
  const feedbackFlags = parseFeedbackFlags(feedback.FeedbackFlags);

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/admin/feedback")}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            {t("common.backToList")}
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">{t("feedbackDetail.title")}</h1>
              <p className="text-sm text-gray-500 mt-1">{t("feedbackDetail.subtitle")}</p>
            </div>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap ${
                feedback.Type === "useful"
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {feedback.Type === "useful" ? t("common.helpful") : t("common.unhelpful")}
            </span>
          </div>
        </div>

        {/* Basic Info */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t("feedbackDetail.basicInfo")}
          </h2>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="text-sm text-gray-600 mb-1">{t("feedbackDetail.feedbackTime")}</div>
              <div className="text-sm text-gray-900">{formatDateTime(feedback.FeedbackTime)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 mb-1">{t("feedbackDetail.conversationId")}</div>
              <div className="text-sm text-gray-900 font-mono">{feedback.ConversationID}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 mb-1">{t("feedbackDetail.username")}</div>
              <div className="text-sm text-gray-900">{feedback.UpdateByName}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 mb-1">
                {t("feedbackDetail.sourceDocuments")}
              </div>
              <div className="text-sm text-gray-900">
                {feedback.SourceDocuments?.length ?? 0} {t("feedbackDetail.documents")}
              </div>
            </div>
          </div>
        </div>

        {/* Question */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t("feedbackDetail.userQuestion")}
          </h2>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-900">{feedback.Question}</p>
          </div>
        </div>

        {/* Answer */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {t("feedbackDetail.systemAnswer")}
            </h2>
            <button
              onClick={() => setIsAnswerExpanded(!isAnswerExpanded)}
              className="text-sm text-[#830051] hover:text-[#6a0042] font-medium cursor-pointer whitespace-nowrap"
            >
              {isAnswerExpanded ? t("common.collapse") : t("common.expand")}
            </button>
          </div>
          <div
            className={`bg-gray-50 rounded-lg p-4 ${!isAnswerExpanded ? "max-h-32 overflow-hidden relative" : ""}`}
          >
            {feedback.SystemAnswer ? (
              <div className="text-sm text-gray-900">
                <XMarkdown content={feedback.SystemAnswer} />
              </div>
            ) : (
              <p className="text-sm text-gray-400">-</p>
            )}
            {!isAnswerExpanded && (
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-gray-50 to-transparent"></div>
            )}
          </div>
        </div>

        {/* Sources */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t("feedbackDetail.sourceDocsSection")}
          </h2>
          <div className="space-y-3">
            {(feedback.SourceDocuments ?? []).map((doc, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <i className="ri-file-text-line text-gray-400 text-lg"></i>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{doc.FileName}</div>
                  </div>
                </div>
              </div>
            ))}
            {(!feedback.SourceDocuments || feedback.SourceDocuments.length === 0) && (
              <p className="text-sm text-gray-400">{t("feedbackDetail.noSourceDocs")}</p>
            )}
          </div>
        </div>

        {/* Useless Feedback Details */}
        {isUseless && (
          <>
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {t("feedbackDetail.unhelpfulReason")}
              </h2>
              <div className="flex flex-wrap gap-2 mb-4">
                {feedbackFlags.map((flag, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-red-100 text-red-700 rounded-lg text-sm font-medium"
                  >
                    {flag}
                  </span>
                ))}
              </div>
              {feedback.FeedbackDescription && (
                <>
                  <div className="text-sm text-gray-600 mb-2">
                    {t("feedbackDetail.userComments")}
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-900">{feedback.FeedbackDescription}</p>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {t("feedbackDetail.internalNote")}
            </h2>
            {!isEditingNote && (
              <button
                onClick={() => setIsEditingNote(true)}
                className="text-sm text-[#830051] hover:text-[#6a0042] font-medium cursor-pointer whitespace-nowrap flex items-center gap-1"
              >
                <i className="ri-edit-line"></i>
                {t("feedbackDetail.editNote")}
              </button>
            )}
          </div>
          {isEditingNote ? (
            <div>
              <textarea
                value={internalNote}
                onChange={(e) => setInternalNote(e.target.value)}
                placeholder={t("feedbackDetail.notePlaceholder")}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#830051] focus:border-transparent resize-none mb-3"
              />
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => {
                    setIsEditingNote(false);
                    setInternalNote(feedback.InternalNote || "");
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t("common.cancel")}
                </button>
                <button
                  onClick={handleSaveNote}
                  className="px-4 py-2 bg-[#830051] text-white rounded-lg text-sm font-medium hover:bg-[#6a0042] transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t("feedback.saveNote")}
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4">
              {internalNote ? (
                <p className="text-sm text-gray-900">{internalNote}</p>
              ) : (
                <p className="text-sm text-gray-400">{t("feedbackDetail.noNote")}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
